'use client';
import React, { Fragment, useRef, useMemo, useState, useEffect } from 'react';
import Image from 'next/image';
import { IKButton } from '@/components/shared/material';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components, noParagraphs, paragraphs } from './PortableTextComponents';
import { MenuItemLink, TrustBadgeType } from '@/types/types';
import PageBreadcrumbs from '@/components/shared/PageBreadcrumbs';
import { m, useInView, AnimatePresence } from 'framer-motion';
import { fadeUp, fadeUpLeft } from '@/assets/framer/animations';
import { CaretRight } from '@phosphor-icons/react';
import YouTube, { YouTubeEvent, YouTubeProps } from 'react-youtube';
import YoutubeVideoBanner from '../shared/YoutubeVideoBanner';
import { useMatchMedia } from '@/lib/matchMedia';
import FormClient from '../shared/form/FormClient';
import { IKDebitCardAnimation } from '../shared/cards';
import MuxVideoPlayer from '../shared/MuxVideoPlayer';
import { useRelativeCoordinates } from '@/hooks/useRelativeCoordinates';
import TrustBadge from '../shared/TrustBadge';

export default function PageBanner(props: {
  data?: {
    sectionId: string;
    breadcrumbs?: { title: string; link: MenuItemLink }[];
    heading?: PortableTextBlock;
    subheading?: PortableTextBlock;
    disclaimerText?: PortableTextBlock;
    buttons?: [
      {
        text?: string;
        useImage?: boolean;
        image?: {
          url?: string;
          alt?: string;
          mediaAlt?: string;
        };
        link: MenuItemLink;
      },
    ];
    video?: {
      youtubeVideo: {
        id: string;
      };
      previewTiming: {
        start: number;
        end: number;
      };
      videoText: PortableTextBlock;
      youtubeThumbnail: { url: string; alt: string; blurDataUrl: string };
    };
    image?: {
      url: string;
      alt: string;
      mediaAlt?: string;
      blurDataUrl: string;
    };
    placeholderImage?: string;
    backgroundImage?: string;
    cards: [
      {
        icon: {
          url: string;
          alt: string;
        };
        title: PortableTextBlock;
        textContent: PortableTextBlock;
        button: {
          text: string;
          link: MenuItemLink;
        };
      },
    ];
    displayOptions: {
      variant: 'default' | 'campaign';
      showAcceptedPaymentMethods: boolean;
      showBackgroundPattern: boolean;
      showAnimatedPattern: boolean;
      background: string;
      imageType: 'center' | 'tall';
      acceptedPaymentMethodsText: PortableTextBlock;
      showDivider: boolean;
      imageBannerFooter: {
        label: string;
        image: {
          url: string;
        };
        imageBannerFooterSize: 'small' | 'large';
      };
      showImageBannerFooter: boolean;
      whiteSpaceCorrection: boolean;
    };
    form: {
      heading?: PortableTextBlock;
      textContent?: PortableTextBlock;
      subText?: PortableTextBlock;
      formId?: string;
      formType?: string;
    };
    type: 'form' | 'video' | 'image' | 'ik-debit';
    tag: string;
    muxVideoId: string;
    showTrustBadge: boolean;
    trustBadge?: TrustBadgeType;
    index: number;
  };
}) {
  // Props
  const sectionId = props.data?.sectionId;
  const title = props.data?.heading;
  const buttons = props.data?.buttons;
  const subheading = props.data?.subheading;
  const disclaimerText = props.data?.disclaimerText;
  const image = props.data?.image;
  const cards = props.data?.cards;
  const video = props.data?.video;
  const breadcrumbs = props.data?.breadcrumbs;
  const isFirstComponent = !props.data?.index || props.data?.index === 0;
  const showDividerWave = props.data?.displayOptions?.showDivider ?? false;
  const isDesktopResolution = useMatchMedia('(min-width:1080px)', true);
  const [, setIsVideoPlaying] = useState(false);

  const [player, _setPlayer] = useState<YouTubeEvent['target'] | null>(null);

  const playerStart = video ? video.previewTiming.start : 0;
  const playerEnd = video ? video.previewTiming.end : 0;

  const videoIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const videoStarted = useRef<boolean>(false);

  const section = useRef(null);
  const videoRef = useRef(null);
  const isInView = useInView(section, { amount: 0.4, once: true });
  const isInVideoInViewIntial = useInView(videoRef, {
    amount: 0.4,
    once: true,
  });
  const isInVideoInView = useInView(videoRef, {
    amount: 0.4,
    once: false,
  });

  useEffect(() => {
    if (isInVideoInView) {
      if (videoIntervalRef.current) {
        clearInterval(videoIntervalRef.current);
        videoIntervalRef.current = null;
      }
      setIsVideoPlaying(false);
      // Check if player state is set and has properties
      if (player && player.g && videoStarted.current) {
        player.seekTo(playerStart);
        player.pauseVideo();
      }

      if (player && player.g && !videoStarted.current) {
        player.playVideo();
        setTimeout(() => {
          player.seekTo(playerStart);
          player.pauseVideo();
        }, 500);
      }
    }

    // }
  }, [
    isInVideoInView,
    player,
    playerEnd,
    playerStart,
    isInView,
    isDesktopResolution,
  ]);

  const { coordinates: mousePosition, handleMouseMove } =
    useRelativeCoordinates(section);

  const showAcceptedPaymentMethods =
    props.data?.displayOptions?.showAcceptedPaymentMethods ?? false;
  const acceptedPaymentMethodsText =
    props.data?.displayOptions?.acceptedPaymentMethodsText;

  const displayOptions = props.data?.displayOptions;
  const displayColor = props.data?.displayOptions?.background;

  const backgroundClass = useMemo(() => {
    switch (displayColor) {
      case 'yellow':
        return 'bg-gradient-yellow';
      case 'light-yellow':
        return 'bg-gradient-light-yellow';
      case 'charcoal':
        return 'bg-gradient-charcoal';
      case 'solid-light-yellow':
        return 'bg-[#fbb800]';
      default:
        return 'bg-gradient-yellow';
    }
  }, [displayColor]);

  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const opts: YouTubeProps['opts'] = {
    height: '390',
    width: '640',
    iframeClassName: 'w-full h-full',
  };

  const setActiveVideoHandler = () => {
    if (video?.youtubeVideo?.id) {
      setActiveVideo(video?.youtubeVideo?.id);
    }
  };

  useEffect(() => {
    if (activeVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [activeVideo]);

  const onPlayerReady: YouTubeProps['onReady'] = (event) => {
    event.target.playVideo();
  };

  useEffect(() => {
    const handleEscKeyPress = (event: KeyboardEvent) => {
      if (
        (event.key === 'Escape' && activeVideo) ||
        (event.key === 'Esc' && activeVideo)
      ) {
        setActiveVideo(null);
      }
    };

    window.addEventListener('keydown', handleEscKeyPress);

    return () => {
      window.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [activeVideo]);

  const isMediaType =
    !props?.data?.type || ['image', 'video'].includes(props?.data?.type);

  const RenderAnimatedPatterns = (
    type: 'default' | 'campaign' | undefined = undefined
  ) => (
    <>
      {/* Teal pattern */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className={`absolute ${type !== 'campaign' ? '-left-8 top-10 z-20 md:-top-8' : 'left-[29rem] hidden sm:block md:top-12 md:z-300'} h-16 w-16 md:z-0`}
        width='67'
        height='67'
        viewBox='0 0 67 67'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 50 - 10 : 0,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.8986 27.3153C27.1852 27.3153 27.4145 27.0858 27.4145 26.7989V14.655H39.9672V26.7989C39.9672 27.0858 40.1965 27.3153 40.4831 27.3153H52.176V39.8226H40.0436C39.757 39.8226 39.5278 40.0521 39.5278 40.339V52.7889H27.7202V40.3963C27.7202 40.1095 27.4909 39.88 27.2043 39.88H14.7471V27.3153H26.8604H26.8986ZM1.25824 40.6258H13.7154V53.4009C13.7154 53.6878 13.9447 53.9173 14.2313 53.9173H26.8986V66.3098C26.8986 66.5967 27.1279 66.8262 27.4145 66.8262H40.4066C40.6932 66.8262 40.9225 66.5967 40.9225 66.3098V53.8408H53.0549C53.3415 53.8408 53.5707 53.6113 53.5707 53.3244V40.5493H65.7031C65.9897 40.5493 66.219 40.3198 66.219 40.033V27.0475C66.219 26.7607 65.9897 26.5312 65.7031 26.5312H54.0102V13.8135C54.0102 13.5266 53.7809 13.2971 53.4943 13.2971H40.7314V1.13403C40.7314 0.847168 40.5022 0.617676 40.2156 0.617676H27.2425C26.9559 0.617676 26.7267 0.847168 26.7267 1.13403V13.2589H13.9638C13.6772 13.2589 13.4479 13.4884 13.4479 13.7752V26.5503H1.25824C0.971644 26.5503 0.742371 26.7798 0.742371 27.0667V40.0521C0.742371 40.339 0.971644 40.5685 1.25824 40.5685'
          fill='#009EAC'
        />
      </m.svg>
      {/* Orange pattern */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className={`${type === 'campaign' ? 'hidden' : ''} absolute -left-32 top-12 z-0 h-10 w-10`}
        width='41'
        height='40'
        viewBox='0 0 41 40'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 30 + 10 : 0,
                y: mousePosition.y ? mousePosition.y / 30 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.4013 25.7783H14.2307V14.3611H26.4013V25.7783ZM0.703548 27.5378H13.3518V39.2801C13.3518 39.567 13.5811 39.7965 13.8677 39.7965H26.8407C27.1273 39.7965 27.3566 39.567 27.3566 39.2801V26.6007H39.8711C40.1576 26.6007 40.3869 26.3712 40.3869 26.0843V13.0798C40.3869 12.7929 40.1576 12.5634 39.8711 12.5634H27.2228V0.821045C27.2228 0.53418 26.9935 0.304688 26.707 0.304688H13.7339C13.4473 0.304688 13.218 0.53418 13.218 0.821045V13.5005H0.703548C0.416956 13.5005 0.187683 13.73 0.187683 14.0168V27.0023C0.187683 27.2892 0.416956 27.5186 0.703548 27.5186'
          fill='#D64C19'
        />
      </m.svg>
      {/* Pink pattern */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className={`absolute ${type !== 'campaign' ? '-right-16 top-20 md:-left-20' : 'top-[25rem] hidden sm:block md:-left-32 xl:top-[32rem] 2xl:top-[25rem]'} z-20 h-32 w-32 sm:h-36 sm:w-36 lg:h-40 lg:w-40`}
        width='142'
        height='143'
        viewBox='0 0 142 143'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 40 : 0,
                y: mousePosition.y ? mousePosition.y / 40 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M64.0003 53.6257C64.4779 53.6257 64.8601 53.2432 64.8601 52.7651V40.602C64.8601 40.602 64.8218 40.5637 64.8218 40.5255H76.8396V52.4973C76.8396 52.9754 77.2217 53.3771 77.7185 53.3771H89.4878V64.8708C89.4878 65.3489 89.889 65.7505 90.3667 65.7505H101.754V76.9H90.3667C89.889 76.9 89.4878 77.2825 89.4878 77.7797V89.2735H77.9095C77.4319 89.2735 77.0306 89.6559 77.0306 90.1532V102.297C77.0306 102.297 77.0689 102.355 77.0689 102.374H65.1084C65.1658 102.259 65.2422 102.163 65.2422 102.049V89.8854C65.2422 89.4073 64.8601 89.0057 64.3824 89.0057H52.8232V77.5885C52.8232 77.1104 52.4411 76.7279 51.9634 76.7279H40.2323V66.0756C40.2323 66.0756 40.175 65.9609 40.1559 65.9035H51.6768C52.1545 65.9035 52.5366 65.521 52.5366 65.0429V53.6257H64.0194H64.0003ZM14.4964 52.0001C14.0187 52.0001 13.6366 52.3826 13.6366 52.8607V65.0238C13.6366 65.0238 13.6939 65.1385 13.713 65.1959H1.69529C1.21763 65.1959 0.816406 65.5975 0.816406 66.0756V78.2196C0.816406 78.6977 1.19853 79.0993 1.69529 79.0993H13.9996V89.7516C13.9996 90.2297 14.3817 90.6122 14.8594 90.6122H26.1893V102.029C26.1893 102.508 26.5714 102.89 27.0491 102.89H39.0668C38.9522 103.062 38.8184 103.253 38.8184 103.464V115.13C38.8184 115.742 39.3152 116.258 39.9266 116.258H51.2947C51.2947 116.258 51.2565 116.296 51.2565 116.334V128.498C51.2565 128.976 51.6386 129.377 52.1354 129.377H64.1722V141.885C64.1722 142.171 64.4015 142.401 64.6881 142.401H77.5465C77.8331 142.401 78.0624 142.171 78.0624 141.885V129.377H89.7171C90.1947 129.377 90.596 128.976 90.596 128.498V116.334C90.596 116.334 90.5769 116.296 90.5769 116.258H102.442C103.053 116.258 103.55 115.761 103.55 115.13V103.464C103.55 103.33 103.454 103.253 103.416 103.139H115.529C116.007 103.139 116.408 102.756 116.408 102.259V90.7652H128.197C128.674 90.7652 129.076 90.3636 129.076 89.8854V78.4108H140.234C140.711 78.4108 141.113 78.0283 141.113 77.5311V65.368C141.113 64.8899 140.711 64.4883 140.234 64.4883H128.713V52.6694C128.713 52.1913 128.331 51.7897 127.834 51.7897H116.045V40.2769C116.045 39.7988 115.663 39.4163 115.166 39.4163H103.875C103.875 39.4163 103.913 39.3589 103.913 39.3398V27.693C103.913 27.0811 103.416 26.5647 102.786 26.5647H90.1374C90.1947 26.45 90.2712 26.3735 90.2712 26.2396V14.0765C90.2712 13.5984 89.8699 13.1968 89.3923 13.1968H77.6994V0.746826C77.6994 0.459961 77.4701 0.230469 77.1835 0.230469H64.3251C64.0385 0.230469 63.8092 0.459961 63.8092 0.746826V13.2542H51.7724C51.2947 13.2542 50.8935 13.6366 50.8935 14.1339V26.297C50.8935 26.4308 50.989 26.5073 51.0272 26.6221H39.4298C38.7993 26.6221 38.3026 27.1193 38.3026 27.7504V39.3971C38.3026 39.531 38.3981 39.6075 38.4363 39.7223H26.6479C26.1702 39.7223 25.769 40.1047 25.769 40.5829V52.0001H14.4391H14.4964Z'
          fill='#D72A4E'
        />
      </m.svg>

      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className={`${type !== 'campaign' ? 'hidden' : ''} absolute right-8 top-28 z-20 h-32 w-32`}
        width='151'
        height='153'
        viewBox='0 0 151 153'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 40 : 0,
                y: mousePosition.y ? mousePosition.y / 40 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M123.304 70.57H135.612V82.5118H123.304V70.57ZM110.403 84.1504H122.71V95.7644H110.403V84.1504ZM96.8456 97.3826H109.153V109.795H96.8456V97.3826ZM69.0967 124.666H81.4045V137.755H69.0967V124.666ZM55.6217 110.348H68.1956V123.806H55.6217V110.348ZM42.208 97.0958H54.5158V109.509H42.208V97.0958ZM29.1426 84.683H40.8975V95.4776H29.1426V84.683ZM40.5289 69.7917H28.7739V57.5632H40.5289V69.7917ZM54.1472 55.9245H41.8394V43.5117H54.1472V55.9245ZM67.827 42.6719H55.2531V29.2144H67.827V42.6719ZM81.0359 28.3541H68.7281V15.2654H81.0359V28.3541ZM95.0025 42.3851H82.6947V29.2144H95.0025V42.3851ZM108.785 55.6378H96.4769V43.2249H108.785V55.6378ZM96.0878 82.4708C96.0878 82.4708 95.9445 82.5322 95.8626 82.5732V70.5495H108.437V82.4913H96.0878V82.4708ZM82.9609 95.7234V84.3962H95.3097C95.3097 84.3962 95.453 84.3348 95.535 84.2938V95.7234H82.9609ZM55.7651 84.3962H68.0728V95.4571H55.7651V84.3962ZM67.7042 69.5049H55.3964V57.5427H67.7042V69.5049ZM68.6667 70.7954H80.9745V82.184H68.6667V70.7954ZM95.1459 69.5049H82.5718V57.2764H95.1459V69.5049ZM81.5889 55.9245H69.0148V43.2454H81.5889V55.9245ZM42.1056 70.8158H53.7786V82.2045H42.1056V70.8158ZM69.3835 97.0958H81.9575V109.775H69.3835V97.0958ZM122.342 69.2386H110.034V57.2764H122.342V69.2386ZM14.9917 70.7134H27.1971V82.3069H14.9917V70.7134ZM95.3711 123.806H83.0633V110.635H95.3711V123.806ZM149.947 84.1299C150.254 84.1299 150.5 83.8841 150.5 83.5769V69.7917C150.5 69.4844 150.254 69.2386 149.947 69.2386H137.23V56.2113C137.23 55.904 136.984 55.6583 136.677 55.6583H123.673V42.9586C123.673 42.6514 123.427 42.4056 123.12 42.4056H109.891V28.9277C109.891 28.6204 109.645 28.3746 109.338 28.3746H95.9241V14.8967C95.9241 14.5894 95.6783 14.3436 95.3711 14.3436H82.5718V0.947571C82.5718 0.640323 82.326 0.394531 82.0189 0.394531H68.2571C67.95 0.394531 67.7042 0.640323 67.7042 0.947571V14.3436H54.4134C54.1063 14.3436 53.8605 14.5894 53.8605 14.8967V28.6409H40.9383C40.6311 28.6409 40.3855 28.8867 40.3855 29.1939V42.6719H27.5247C27.2175 42.6719 26.9718 42.9177 26.9718 43.2249V55.8221H14.4592C14.152 55.8221 13.9064 56.0679 13.9064 56.3752V69.9555H0.718007C0.410825 69.9555 0.165039 70.2013 0.165039 70.5086V84.2938C0.165039 84.601 0.410825 84.8468 0.718007 84.8468H14.275V96.6247C14.275 96.9319 14.5206 97.1777 14.8278 97.1777H27.3405V109.775C27.3405 110.082 27.5861 110.328 27.8933 110.328H40.7541V123.806C40.7541 124.113 40.9998 124.359 41.3069 124.359H54.2292V138.103C54.2292 138.41 54.4749 138.656 54.782 138.656H68.0728V152.052C68.0728 152.36 68.3186 152.605 68.6257 152.605H82.408C82.7151 152.605 82.9609 152.36 82.9609 152.052V138.656H95.7602C96.0674 138.656 96.3132 138.41 96.3132 138.103V124.625H109.727C110.034 124.625 110.28 124.379 110.28 124.072V110.594H123.509C123.816 110.594 124.062 110.348 124.062 110.041V97.3416H137.066C137.373 97.3416 137.619 97.0958 137.619 96.7886V84.1094H149.968L149.947 84.1299Z'
          fill='#D64C19'
        />
      </m.svg>
    </>
  );

  const RenderMediaContent = () => {
    if (!isMediaType) return null;

    const isTallImageType = displayOptions?.imageType === 'tall';
    const hasYouTubeVideo = video?.youtubeVideo?.id;
    const hasMuxVideo = !!props?.data?.muxVideoId;

    if (isTallImageType) {
      return (
        <div className='relative h-full max-h-[700px] w-full md:max-h-full'>
          {displayOptions?.showAnimatedPattern && RenderAnimatedPatterns()}

          {/* Video content */}
          {hasYouTubeVideo ? (
            <div
              ref={videoRef}
              className='relative z-[8] aspect-[1/1] h-full w-full transform md:translate-y-16 lg:aspect-[14/9]'
            >
              {isInVideoInViewIntial && (
                <YoutubeVideoBanner
                  videoRef={videoRef}
                  className='aspect-[1/1] cursor-pointer'
                  showVideo={setActiveVideoHandler}
                  video={video}
                  image={image}
                />
              )}
            </div>
          ) : hasMuxVideo ? (
            <MuxVideoPlayer
              muxVideoId={props?.data?.muxVideoId as string}
              className='relative z-[8] aspect-[1/1] h-full w-full transform md:-translate-y-7'
              image={image}
            />
          ) : (
            image &&
            image.url && (
              <Image
                quality={95}
                loading='eager'
                priority={true}
                sizes='(max-width: 768px) 95vw, (max-width: 1024px) 50vw, 655px'
                alt={image.mediaAlt || image.alt || 'iKhokha'}
                width='655'
                height='740'
                placeholder='blur'
                blurDataURL={image.blurDataUrl}
                src={image.url}
                className={`relative z-10 h-full w-full rounded-t-[20px] object-cover shadow-[-2px_21px_31px_#DEA403] ${video?.youtubeVideo?.id ? 'hover:cursor-pointer' : ''}`}
              />
            )
          )}
        </div>
      );
    }

    return (
      <div
        className={`${showAcceptedPaymentMethods ? 'mb-32' : 'mb-12'} relative flex h-full max-h-[700px] w-full flex-col items-center justify-center gap-6 sm:gap-6 md:max-h-full md:items-start xl:gap-8`}
      >
        {/* Video or Image */}
        {hasMuxVideo ? (
          <MuxVideoPlayer
            muxVideoId={props?.data?.muxVideoId as string}
            className='relative z-[8] aspect-[1/1] h-full w-full transform md:translate-y-16 lg:aspect-[14/9]'
            image={image}
          />
        ) : (
          image &&
          image.url && (
            <Image
              onClick={() => setActiveVideoHandler()}
              quality={95}
              loading='eager'
              sizes='(max-width: 768px) 95vw, (max-width: 1024px) 50vw, 655px'
              priority={true}
              alt={image.mediaAlt || image.alt || 'iKhokha'}
              width='655'
              height='740'
              placeholder='blur'
              blurDataURL={image.blurDataUrl}
              src={image.url}
              className={`relative z-10 w-full object-contain ${video?.youtubeVideo?.id ? 'hover:cursor-pointer' : ''}`}
            />
          )
        )}
      </div>
    );
  };

  const getBannerFooterClasses = (imageBannerFooter: any) => {
    // props?.data?.type === 'ik-debit'
    const { label, imageBannerFooterSize } = imageBannerFooter;

    if (props?.data?.type === 'ik-debit' && imageBannerFooterSize !== 'large') {
      return 'sm:ml-48 md:ml-8 lg:ml-40 lg:mt-8 mt-4';
    }

    if (label && imageBannerFooterSize === 'large') {
      return 'ml-0 mt-0 w-full flex-col items-center justify-center lg:mb-6';
    }

    if (label && imageBannerFooterSize !== 'large') {
      return 'sm:ml-48 md:ml-8 lg:ml-40 lg:mt-8 mt-4 lg:mb-6';
    }

    return 'mt-2 w-full justify-center md:mt-4 lg:mb-14';
  };

  return (
    <>
      <section
        id={sectionId}
        ref={section}
        className={`${backgroundClass} ${displayOptions?.imageType !== 'tall' || (displayOptions?.imageType !== 'tall' && cards && cards.length) || (displayOptions?.imageType !== 'tall' && buttons && buttons.some((button) => button.text)) ? 'section-padding-b' : ''} relative overflow-x-clip bg-gradient-to-b pt-24 md:pt-28 xl:pt-32 2xl:pt-36`}
        onMouseMove={(e) =>
          displayOptions?.showAnimatedPattern ? handleMouseMove(e) : null
        }
      >
        {displayOptions?.whiteSpaceCorrection && (
          <div
            className={`${backgroundClass} absolute left-0 top-0 z-0 h-[120%] w-full bg-gradient-to-b`}
          />
        )}

        <div className='container-padding container relative mx-auto'>
          <div className='flex flex-col items-stretch gap-x-7 md:flex-row md:justify-start'>
            <div
              className={`relative z-10 flex flex-grow flex-col justify-start pb-9 ${displayOptions?.variant === 'campaign' ? 'md:pb-28' : 'md:pb-20'} ${props.data?.tag ? 'space-y-3.5' : 'space-y-6'} md:w-1/2 md:pt-6 ${displayColor === 'charcoal' ? 'text-white' : 'text-charcoal'}`}
            >
              {/* Mobile pattern  */}
              <div className='absolute -right-5 z-0 h-24 w-32 sm:-right-8 sm:h-32 sm:w-40 md:hidden'>
                <svg
                  className='h-full w-full'
                  xmlns='http://www.w3.org/2000/svg'
                  width='82'
                  height='98'
                  viewBox='0 0 38 98'
                >
                  <path
                    d='M27.0144 25.5635H14.8438V14.1462H27.0144V25.5635ZM1.31665 27.3229H13.9649V39.0653C13.9649 39.3521 14.1942 39.5816 14.4807 39.5816H27.4538C27.7404 39.5816 27.9697 39.3521 27.9697 39.0653V26.3858H40.4842C40.7707 26.3858 41 26.1563 41 25.8695V12.8649C41 12.578 40.7707 12.3486 40.4842 12.3486H27.8359V0.606201C27.8359 0.319336 27.6066 0.0898438 27.32 0.0898438H14.347C14.0604 0.0898438 13.8311 0.319336 13.8311 0.606201V13.2856H1.31665C1.03005 13.2856 0.800781 13.5151 0.800781 13.802V26.7874C0.800781 27.0743 1.03005 27.3038 1.31665 27.3038'
                    fill='#D64C19'
                  />
                  <path
                    d='M82 31.5042H70.1564C69.8698 31.5042 69.6405 31.7336 69.6405 32.0205V44.1453H56.8777C56.5911 44.1453 56.3618 44.3748 56.3618 44.6617V57.4368H44.1721C43.8855 57.4368 43.6562 57.6663 43.6562 57.9531V70.9386C43.6562 71.2254 43.8855 71.4549 44.1721 71.4549V71.5123H56.6293V84.2874C56.6293 84.5742 56.8586 84.8037 57.1452 84.8037H69.8125V97.1963C69.8125 97.4832 70.0418 97.7127 70.3284 97.7127H82V83.6754H70.6341V71.2828C70.6341 70.9959 70.4048 70.7665 70.1182 70.7665H57.661V58.2017H69.8125C70.0991 58.2017 70.3284 57.9723 70.3284 57.6854V45.5414H82V31.5042Z'
                    fill='#D64C19'
                  />
                </svg>
              </div>

              {/* Breadcrumbs */}
              {breadcrumbs && breadcrumbs?.length > 0 && isFirstComponent && (
                <div>
                  <PageBreadcrumbs
                    color={displayColor === 'charcoal' ? 'white' : 'charcoal'}
                    breadcrumbs={breadcrumbs}
                  />
                </div>
              )}

              {props.data?.tag && (
                <span className='mt-2 flex h-7 w-44 items-center justify-center rounded bg-pink-500 text-center text-sm font-extrabold tracking-[0.15em] text-white shadow sm:!mt-14'>
                  {props.data?.tag}
                </span>
              )}
              <h1
                className={`${displayOptions?.variant === 'campaign' ? 'heading-xxl xl:text-8xl' : 'heading-xl font-medium'} relative z-10 pr-10 font-display tracking-tighter md:pr-0 ${
                  displayOptions?.background === 'charcoal'
                    ? 'text-white'
                    : 'text-charcoal'
                }`}
              >
                {title && (
                  <PortableText
                    components={components}
                    value={title}
                  ></PortableText>
                )}
              </h1>

              <h2
                className={`heading-xs [&_ul]:list-[initial] ${displayOptions?.variant !== 'campaign' ? 'max-w-lg [&_strong]:text-pink' : 'max-w-2xl'} [&_ul]:pl-8 ${
                  displayOptions?.background === 'charcoal'
                    ? 'text-white'
                    : 'text-charcoal'
                }`}
              >
                {subheading && (
                  <PortableText
                    components={components}
                    value={subheading}
                  ></PortableText>
                )}
              </h2>

              {/* Buttons */}
              {buttons && buttons.length > 0 && (
                <div className='flex flex-wrap items-center gap-6 md:pt-6'>
                  {buttons &&
                    buttons.length > 0 &&
                    buttons.map((button, index) => (
                      <IKButton
                        key={`home-banner-button-${index}`}
                        link={button.link}
                        color={
                          displayColor === 'charcoal' ? 'yellow' : 'charcoal'
                        }
                        variant={
                          index === 0 && !button.useImage ? 'filled' : 'text'
                        }
                        size={index === 0 ? 'md' : 'sm'}
                        className={`flex items-center gap-1 ${button.useImage && '!bg-none !shadow-none'} ${
                          index === 0
                            ? ''
                            : 'bg-gradient-line relative overflow-x-hidden !rounded-none bg-right-bottom bg-no-repeat !py-2 transition-all duration-500 hover:!bg-left-bottom hover:transition-all hover:duration-500'
                        }`}
                      >
                        {button.useImage && button?.image?.url ? (
                          <Image
                            src={button.image.url}
                            alt={button.text || ''}
                            width={200}
                            height={100}
                            className='h-7 w-44'
                          />
                        ) : (
                          <>
                            <span>{button.text}</span>
                            {index === 0 && (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='25'
                                height='25'
                                viewBox='0 0 25 25'
                                fill='none'
                                aria-label='Arrow Icon'
                              >
                                <path
                                  d='M17.5854 12.5703L10.0854 20.0704C10.0157 20.14 9.93302 20.1953 9.84197 20.233C9.75093 20.2707 9.65335 20.2901 9.5548 20.2901C9.45625 20.2901 9.35867 20.2707 9.26763 20.233C9.17658 20.1953 9.09386 20.14 9.02417 20.0704C8.95449 20.0007 8.89922 19.9179 8.8615 19.8269C8.82379 19.7359 8.80438 19.6383 8.80438 19.5397C8.80438 19.4412 8.82379 19.3436 8.8615 19.2526C8.89922 19.1615 8.95449 19.0788 9.02417 19.0091L15.9945 12.0397L9.02417 5.07035C8.88344 4.92962 8.80438 4.73875 8.80438 4.53972C8.80438 4.3407 8.88344 4.14983 9.02417 4.0091C9.1649 3.86837 9.35578 3.78931 9.5548 3.78931C9.75382 3.78931 9.94469 3.86837 10.0854 4.0091L17.5854 11.5091C17.6552 11.5788 17.7105 11.6615 17.7482 11.7525C17.786 11.8436 17.8054 11.9412 17.8054 12.0397C17.8054 12.1383 17.786 12.2359 17.7482 12.3269C17.7105 12.418 17.6552 12.5007 17.5854 12.5703Z'
                                  fill='currentColor'
                                />
                              </svg>
                            )}
                          </>
                        )}
                      </IKButton>
                    ))}
                </div>
              )}

              {disclaimerText && (
                <p className='absolute bottom-0 left-0 mt-auto hidden w-full pb-2 text-xs md:block lg:pb-0'>
                  <PortableText
                    components={noParagraphs}
                    value={disclaimerText}
                  />
                </p>
              )}

              <TrustBadge
                trustBadge={props?.data?.trustBadge}
                showTrustBadge={props?.data?.showTrustBadge}
                className='!bottom-0 hidden md:flex'
              />
            </div>

            <div
              className={`${props.data?.tag && '!mt-14'} relative flex flex-grow flex-col items-center ${displayOptions?.variant === 'campaign' ? '-mb-20 md:mb-0 md:items-start' : 'sm:items-start'} md:w-1/2 xl:pl-10 2xl:pl-16`}
            >
              {props?.data?.type === 'form' && (
                <>
                  {displayOptions?.showAnimatedPattern &&
                    RenderAnimatedPatterns(displayOptions.variant)}
                  <FormClient
                    form={props?.data?.form}
                    formClassName={`${showAcceptedPaymentMethods ? 'mb-32 sm:mb-16' : 'mb-16  sm:mb-4 lg:mb-0'} `}
                    subTextClassName={`${showAcceptedPaymentMethods ? 'mb-40 lg:mb-0 ' : ''}`}
                  />
                </>
              )}
              {props?.data?.type === 'ik-debit' && (
                <div
                  className={`${showAcceptedPaymentMethods ? 'mb-32' : 'mb-6'} relative flex h-full max-h-[700px] w-full flex-col items-center justify-center gap-6 sm:gap-6 md:max-h-full md:items-start xl:gap-8`}
                >
                  <IKDebitCardAnimation />
                </div>
              )}

              {RenderMediaContent()}

              {props?.data?.displayOptions?.showImageBannerFooter &&
                props?.data?.displayOptions?.imageBannerFooter && (
                  <div
                    className={`mb-16 flex items-center gap-4 ${getBannerFooterClasses(
                      props?.data?.displayOptions?.imageBannerFooter
                    )} md:mb-0`}
                  >
                    {props?.data?.displayOptions?.imageBannerFooter.label && (
                      <span className='font-display text-[26px] font-semibold tracking-[-0.081]'>
                        {props?.data?.displayOptions?.imageBannerFooter.label}
                      </span>
                    )}
                    <Image
                      src={
                        props?.data?.displayOptions?.imageBannerFooter.image.url
                      }
                      alt={props?.data?.displayOptions?.imageBannerFooter.label}
                      width={
                        props?.data?.displayOptions?.imageBannerFooter
                          .imageBannerFooterSize === 'large'
                          ? 500
                          : 130
                      }
                      height={32}
                    />
                  </div>
                )}

              {disclaimerText && (
                <p className='mt-auto block w-full pb-10 pt-6 text-xs sm:pb-6 md:hidden lg:pb-0'>
                  <PortableText
                    components={noParagraphs}
                    value={disclaimerText}
                  />
                </p>
              )}
              <div className='w-full px-4'>
                <TrustBadge
                  trustBadge={props?.data?.trustBadge}
                  showTrustBadge={props?.data?.showTrustBadge}
                  className='!bottom-0 flex md:hidden'
                />
              </div>
            </div>
          </div>

          {cards && cards.length > 0 && (
            <div className='relative mb-10 pt-10 md:mb-0'>
              <div className='absolute right-20 hidden md:-top-[140px] md:block lg:-top-[140px] xl:-top-[58%]'>
                {/* Floating patterns */}
                {/* Teal pattern */}
                <m.svg
                  initial='hidden'
                  variants={fadeUpLeft}
                  transition={{
                    delay: 0.3,
                    type: 'tween',
                  }}
                  xmlns='http://www.w3.org/2000/svg'
                  className='absolute -left-8 top-10 z-0 h-16 w-16 md:-top-8 md:z-0'
                  width='67'
                  height='67'
                  viewBox='0 0 67 67'
                  fill='none'
                  animate={{
                    ...(isInView
                      ? {
                          opacity: 1,
                          x: 0,
                          y: 0,
                        }
                      : fadeUp.hidden),
                  }}
                >
                  <path
                    d='M26.8986 27.3153C27.1852 27.3153 27.4145 27.0858 27.4145 26.7989V14.655H39.9672V26.7989C39.9672 27.0858 40.1965 27.3153 40.4831 27.3153H52.176V39.8226H40.0436C39.757 39.8226 39.5278 40.0521 39.5278 40.339V52.7889H27.7202V40.3963C27.7202 40.1095 27.4909 39.88 27.2043 39.88H14.7471V27.3153H26.8604H26.8986ZM1.25824 40.6258H13.7154V53.4009C13.7154 53.6878 13.9447 53.9173 14.2313 53.9173H26.8986V66.3098C26.8986 66.5967 27.1279 66.8262 27.4145 66.8262H40.4066C40.6932 66.8262 40.9225 66.5967 40.9225 66.3098V53.8408H53.0549C53.3415 53.8408 53.5707 53.6113 53.5707 53.3244V40.5493H65.7031C65.9897 40.5493 66.219 40.3198 66.219 40.033V27.0475C66.219 26.7607 65.9897 26.5312 65.7031 26.5312H54.0102V13.8135C54.0102 13.5266 53.7809 13.2971 53.4943 13.2971H40.7314V1.13403C40.7314 0.847168 40.5022 0.617676 40.2156 0.617676H27.2425C26.9559 0.617676 26.7267 0.847168 26.7267 1.13403V13.2589H13.9638C13.6772 13.2589 13.4479 13.4884 13.4479 13.7752V26.5503H1.25824C0.971644 26.5503 0.742371 26.7798 0.742371 27.0667V40.0521C0.742371 40.339 0.971644 40.5685 1.25824 40.5685'
                    fill='#009EAC'
                  />
                </m.svg>
                {/* Orange pattern */}
                <m.svg
                  initial='hidden'
                  variants={fadeUpLeft}
                  transition={{
                    delay: 0.3,
                    type: 'tween',
                  }}
                  xmlns='http://www.w3.org/2000/svg'
                  className='absolute -left-32 top-12 z-0 h-10 w-10'
                  width='41'
                  height='40'
                  viewBox='0 0 41 40'
                  fill='none'
                  animate={{
                    ...(isInView
                      ? {
                          opacity: 1,
                          x: 0,
                          y: 0,
                        }
                      : fadeUp.hidden),
                  }}
                >
                  <path
                    d='M26.4013 25.7783H14.2307V14.3611H26.4013V25.7783ZM0.703548 27.5378H13.3518V39.2801C13.3518 39.567 13.5811 39.7965 13.8677 39.7965H26.8407C27.1273 39.7965 27.3566 39.567 27.3566 39.2801V26.6007H39.8711C40.1576 26.6007 40.3869 26.3712 40.3869 26.0843V13.0798C40.3869 12.7929 40.1576 12.5634 39.8711 12.5634H27.2228V0.821045C27.2228 0.53418 26.9935 0.304688 26.707 0.304688H13.7339C13.4473 0.304688 13.218 0.53418 13.218 0.821045V13.5005H0.703548C0.416956 13.5005 0.187683 13.73 0.187683 14.0168V27.0023C0.187683 27.2892 0.416956 27.5186 0.703548 27.5186'
                    fill='#D64C19'
                  />
                </m.svg>
                {/* Pink pattern */}
                <m.svg
                  initial='hidden'
                  variants={fadeUpLeft}
                  transition={{
                    delay: 0.3,
                    type: 'tween',
                  }}
                  xmlns='http://www.w3.org/2000/svg'
                  className='absolute -right-16 top-20 z-0 h-32 w-32 sm:h-36 sm:w-36 md:-left-20 lg:h-40 lg:w-40'
                  width='142'
                  height='143'
                  viewBox='0 0 142 143'
                  fill='none'
                  animate={{
                    ...(isInView
                      ? {
                          opacity: 1,
                          x: 0,
                          y: 0,
                        }
                      : fadeUp.hidden),
                  }}
                >
                  <path
                    d='M64.0003 53.6257C64.4779 53.6257 64.8601 53.2432 64.8601 52.7651V40.602C64.8601 40.602 64.8218 40.5637 64.8218 40.5255H76.8396V52.4973C76.8396 52.9754 77.2217 53.3771 77.7185 53.3771H89.4878V64.8708C89.4878 65.3489 89.889 65.7505 90.3667 65.7505H101.754V76.9H90.3667C89.889 76.9 89.4878 77.2825 89.4878 77.7797V89.2735H77.9095C77.4319 89.2735 77.0306 89.6559 77.0306 90.1532V102.297C77.0306 102.297 77.0689 102.355 77.0689 102.374H65.1084C65.1658 102.259 65.2422 102.163 65.2422 102.049V89.8854C65.2422 89.4073 64.8601 89.0057 64.3824 89.0057H52.8232V77.5885C52.8232 77.1104 52.4411 76.7279 51.9634 76.7279H40.2323V66.0756C40.2323 66.0756 40.175 65.9609 40.1559 65.9035H51.6768C52.1545 65.9035 52.5366 65.521 52.5366 65.0429V53.6257H64.0194H64.0003ZM14.4964 52.0001C14.0187 52.0001 13.6366 52.3826 13.6366 52.8607V65.0238C13.6366 65.0238 13.6939 65.1385 13.713 65.1959H1.69529C1.21763 65.1959 0.816406 65.5975 0.816406 66.0756V78.2196C0.816406 78.6977 1.19853 79.0993 1.69529 79.0993H13.9996V89.7516C13.9996 90.2297 14.3817 90.6122 14.8594 90.6122H26.1893V102.029C26.1893 102.508 26.5714 102.89 27.0491 102.89H39.0668C38.9522 103.062 38.8184 103.253 38.8184 103.464V115.13C38.8184 115.742 39.3152 116.258 39.9266 116.258H51.2947C51.2947 116.258 51.2565 116.296 51.2565 116.334V128.498C51.2565 128.976 51.6386 129.377 52.1354 129.377H64.1722V141.885C64.1722 142.171 64.4015 142.401 64.6881 142.401H77.5465C77.8331 142.401 78.0624 142.171 78.0624 141.885V129.377H89.7171C90.1947 129.377 90.596 128.976 90.596 128.498V116.334C90.596 116.334 90.5769 116.296 90.5769 116.258H102.442C103.053 116.258 103.55 115.761 103.55 115.13V103.464C103.55 103.33 103.454 103.253 103.416 103.139H115.529C116.007 103.139 116.408 102.756 116.408 102.259V90.7652H128.197C128.674 90.7652 129.076 90.3636 129.076 89.8854V78.4108H140.234C140.711 78.4108 141.113 78.0283 141.113 77.5311V65.368C141.113 64.8899 140.711 64.4883 140.234 64.4883H128.713V52.6694C128.713 52.1913 128.331 51.7897 127.834 51.7897H116.045V40.2769C116.045 39.7988 115.663 39.4163 115.166 39.4163H103.875C103.875 39.4163 103.913 39.3589 103.913 39.3398V27.693C103.913 27.0811 103.416 26.5647 102.786 26.5647H90.1374C90.1947 26.45 90.2712 26.3735 90.2712 26.2396V14.0765C90.2712 13.5984 89.8699 13.1968 89.3923 13.1968H77.6994V0.746826C77.6994 0.459961 77.4701 0.230469 77.1835 0.230469H64.3251C64.0385 0.230469 63.8092 0.459961 63.8092 0.746826V13.2542H51.7724C51.2947 13.2542 50.8935 13.6366 50.8935 14.1339V26.297C50.8935 26.4308 50.989 26.5073 51.0272 26.6221H39.4298C38.7993 26.6221 38.3026 27.1193 38.3026 27.7504V39.3971C38.3026 39.531 38.3981 39.6075 38.4363 39.7223H26.6479C26.1702 39.7223 25.769 40.1047 25.769 40.5829V52.0001H14.4391H14.4964Z'
                    fill='#D72A4E'
                  />
                </m.svg>
              </div>
              <div className='relative z-10 grid gap-5 md:grid-cols-2 md:gap-8 lg:grid-cols-3 lg:gap-6 xl:gap-8 2xl:gap-14'>
                {cards &&
                  cards.map((card, index) => (
                    <Fragment key={`card-${index}`}>
                      <div className='flex flex-col gap-4 rounded-[24px] bg-white p-6 shadow-lg shadow-black/10'>
                        {card.title && (
                          <div className='flex items-center gap-3 md:gap-4'>
                            {card?.icon && card?.icon?.url && (
                              <div className='bg-light-blue flex h-10 w-10 items-center justify-center rounded-full bg-yellow-150 p-2.5 md:h-12 md:w-12'>
                                <Image
                                  src={card.icon.url}
                                  alt={card.icon.alt}
                                  width={24}
                                  height={24}
                                  className='h-full w-full object-contain'
                                />
                              </div>
                            )}
                            <h3 className='text-2xl font-bold'>
                              <PortableText
                                value={card.title}
                                components={noParagraphs}
                              />
                            </h3>
                          </div>
                        )}
                        {card.textContent && (
                          <div className='max-w-[300px] [&_a]:text-teal [&_a]:underline'>
                            <PortableText
                              value={card.textContent}
                              components={paragraphs}
                            />
                          </div>
                        )}
                        {card.button &&
                          card.button.text &&
                          card.button.link && (
                            <div className='mt-auto'>
                              <IKButton
                                link={card.button.link}
                                color='light-blue'
                                size='sm'
                                className='group flex items-center justify-center gap-2 px-5'
                              >
                                <span>{card.button.text}</span>
                                <span>
                                  <CaretRight weight='bold' />
                                </span>
                              </IKButton>
                            </div>
                          )}
                      </div>
                    </Fragment>
                  ))}
              </div>
            </div>
          )}
        </div>

        {showAcceptedPaymentMethods && (
          <div className='container-padding absolute z-50 w-full -translate-y-[80%] transform items-center justify-center md:bottom-4 md:mb-0 md:flex md:translate-y-[30%] md:pb-0'>
            <div className='bg-gradient-charcoal flex flex-col items-center justify-center gap-2 rounded-3xl bg-gradient-to-b px-5 py-7 md:min-w-[530px] md:px-10'>
              {acceptedPaymentMethodsText && (
                <h4 className='text-gray-300'>
                  <PortableText
                    value={acceptedPaymentMethodsText}
                    components={noParagraphs}
                  />
                </h4>
              )}
              <svg
                width='255'
                height='27'
                viewBox='0 0 255 27'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-details='Accepted payment methods: Visa, Mastercard, EFT, Google Pay'
              >
                <g clipPath='url(#clip0_131_13414)'>
                  <path
                    d='M17.958 6.59294H13.8456L10.0428 16.2253L9.62517 14.2705L8.27037 7.78334C8.04717 6.88334 7.36197 6.62774 6.50757 6.59294H0.248373L0.182373 6.87254C1.60712 7.20414 2.97984 7.729 4.26238 8.43254L7.72677 20.7025H11.8392L17.958 6.59294ZM23.4168 6.59294H19.5276L17.0952 20.7157H20.976L23.4168 6.59294ZM31.254 12.2545C29.892 11.6113 29.0568 11.1745 29.0568 10.5097C29.0772 9.90974 29.7672 9.29174 31.2948 9.29174C32.2854 9.2633 33.2708 9.44573 34.1856 9.82694L34.536 9.98174L35.0688 6.93014C33.9525 6.52909 32.7749 6.32604 31.5888 6.33014C27.7488 6.33014 25.0488 8.25014 25.0272 10.9993C24.9984 13.0273 26.964 14.1553 28.434 14.8393C29.9328 15.5257 30.4488 15.9829 30.4488 16.5925C30.4284 17.5345 29.226 17.9725 28.116 17.9725C26.857 18.0069 25.6073 17.748 24.4656 17.2165L23.9532 16.9921L23.412 20.1637C24.7995 20.6696 26.266 20.9244 27.7428 20.9161C31.83 20.9281 34.4892 19.0273 34.518 16.0993C34.536 14.4877 33.492 13.2625 31.254 12.2545ZM45.0456 6.59294H42.036C41.6155 6.53628 41.1881 6.62046 40.8206 6.83235C40.4531 7.04424 40.166 7.37189 40.0044 7.76414L34.2276 20.7157H38.3076C38.3076 20.7157 38.976 18.9709 39.1212 18.5989H44.1132C44.2332 19.0993 44.5752 20.7157 44.5752 20.7157H48.1752L45.0456 6.59294ZM43.476 15.7057H40.2468C40.5648 14.8993 41.7948 11.7541 41.7948 11.7541C41.7744 11.7817 42.1128 10.9285 42.3072 10.4005L42.5796 11.6185C42.5796 11.6185 43.3164 15.0001 43.476 15.7057Z'
                    fill='#EFE9DB'
                  />
                </g>
                <path
                  d='M79.4265 26.8084V25.0422C79.4369 24.8937 79.4155 24.7446 79.3637 24.605C79.3119 24.4654 79.231 24.3385 79.1262 24.2326C79.0215 24.1268 78.8954 24.0444 78.7564 23.9912C78.6173 23.9379 78.4685 23.9149 78.3199 23.9237C78.1253 23.9111 77.9309 23.951 77.7569 24.039C77.583 24.127 77.4358 24.26 77.3306 24.4242C77.2351 24.2648 77.0983 24.1341 76.9346 24.046C76.7709 23.9579 76.5864 23.9156 76.4007 23.9237C76.2388 23.915 76.0774 23.9491 75.9328 24.0224C75.7881 24.0956 75.6652 24.2055 75.5764 24.3412V23.9949H74.9643V26.8084H75.5823V25.2545C75.5688 25.1581 75.5769 25.06 75.6059 24.9671C75.6349 24.8741 75.6841 24.7887 75.7499 24.7171C75.8158 24.6454 75.8968 24.5893 75.9869 24.5526C76.0771 24.5159 76.1742 24.4996 76.2714 24.5049C76.6771 24.5049 76.8835 24.7706 76.8835 25.2474V26.8084H77.5014V25.2545C77.4893 25.1583 77.4982 25.0605 77.5278 24.9681C77.5573 24.8756 77.6067 24.7907 77.6724 24.7193C77.7382 24.6479 77.8187 24.5918 77.9084 24.5548C77.9981 24.5177 78.0948 24.5007 78.1917 24.5049C78.6105 24.5049 78.8074 24.7706 78.8074 25.2474V26.8084H79.4265ZM88.5751 23.9949H87.5752V23.1397H86.9548V23.9949H86.3831V24.5524H86.9548V25.8357C86.9548 26.4893 87.2063 26.8784 87.9322 26.8784C88.2026 26.8794 88.4678 26.8042 88.6973 26.6613L88.5205 26.1358C88.3567 26.2342 88.1706 26.2893 87.9797 26.2959C87.6725 26.2959 87.5728 26.1074 87.5728 25.8215V24.5548H88.5727L88.5751 23.9949ZM93.7941 23.9237C93.6446 23.9193 93.4967 23.9553 93.366 24.0279C93.2353 24.1006 93.1267 24.2072 93.0515 24.3365V23.9949H92.4466V26.8084H93.0563V25.2308C93.0563 24.7658 93.2567 24.5061 93.6588 24.5061C93.7896 24.5046 93.9193 24.5288 94.0408 24.5773L94.2294 23.9996C94.089 23.9508 93.9415 23.9251 93.7929 23.9237M85.8968 24.2179C85.5502 24.0119 85.1517 23.9097 84.7487 23.9237C84.037 23.9237 83.578 24.2653 83.578 24.824C83.578 25.283 83.9196 25.5653 84.5494 25.6543L84.8376 25.6969C85.1721 25.7432 85.3322 25.831 85.3322 25.9912C85.3322 26.2082 85.1104 26.3316 84.6905 26.3316C84.354 26.3415 84.024 26.2379 83.7535 26.0374L83.4653 26.5118C83.8218 26.7607 84.2488 26.8887 84.6834 26.8771C85.4971 26.8771 85.968 26.494 85.968 25.9591C85.968 25.4645 85.5967 25.2059 84.9835 25.1181L84.6953 25.0754C84.4308 25.041 84.2208 24.9888 84.2208 24.8003C84.2208 24.5939 84.4201 24.4693 84.7558 24.4693C85.0641 24.4728 85.3665 24.5544 85.6347 24.7066L85.8968 24.2179ZM102.3 23.9237C102.151 23.9197 102.003 23.956 101.873 24.0286C101.743 24.1012 101.635 24.2076 101.56 24.3365V23.9949H100.951V26.8084H101.564V25.2308C101.564 24.7658 101.765 24.5061 102.165 24.5061C102.296 24.5045 102.426 24.5287 102.548 24.5773L102.735 23.9996C102.595 23.9508 102.447 23.9251 102.299 23.9237M94.4096 25.4016C94.4019 25.5997 94.4356 25.7971 94.5086 25.9814C94.5816 26.1656 94.6923 26.3326 94.8335 26.4716C94.9748 26.6106 95.1435 26.7186 95.3288 26.7887C95.5142 26.8587 95.7122 26.8892 95.9101 26.8784C96.278 26.8967 96.6394 26.7765 96.9231 26.5415L96.6301 26.0457C96.4189 26.2078 96.1609 26.2968 95.8947 26.2995C95.6656 26.2851 95.4505 26.1839 95.2933 26.0166C95.1362 25.8492 95.0487 25.6283 95.0487 25.3987C95.0487 25.1691 95.1362 24.9481 95.2933 24.7808C95.4505 24.6134 95.6656 24.5122 95.8947 24.4978C96.1608 24.5008 96.4188 24.5898 96.6301 24.7516L96.9231 24.2558C96.6389 24.022 96.2776 23.9028 95.9101 23.9214C95.7121 23.9105 95.514 23.941 95.3286 24.0111C95.1431 24.0813 94.9743 24.1894 94.833 24.3285C94.6918 24.4677 94.5812 24.6348 94.5082 24.8191C94.4353 25.0035 94.4017 25.2011 94.4096 25.3993M100.143 23.9925H99.5314V24.3341C99.427 24.1994 99.2918 24.0916 99.1373 24.0198C98.9827 23.948 98.8132 23.9143 98.6429 23.9214C98.2637 23.94 97.9061 24.1038 97.6442 24.3788C97.3824 24.6538 97.2364 25.0189 97.2364 25.3987C97.2364 25.7784 97.3824 26.1436 97.6442 26.4186C97.9061 26.6936 98.2637 26.8574 98.6429 26.876C98.8131 26.8832 98.9825 26.8496 99.1371 26.778C99.2916 26.7064 99.4269 26.5989 99.5314 26.4644V26.806H100.143V23.9925ZM97.866 25.3993C97.8753 25.2329 97.9331 25.0729 98.0322 24.939C98.1314 24.8052 98.2676 24.7033 98.424 24.646C98.5805 24.5886 98.7502 24.5784 98.9124 24.6164C99.0746 24.6545 99.2221 24.7393 99.3367 24.8602C99.4512 24.9811 99.5279 25.133 99.5571 25.297C99.5863 25.461 99.5669 25.63 99.5012 25.7831C99.4355 25.9362 99.3264 26.0667 99.1873 26.1585C99.0483 26.2502 98.8854 26.2992 98.7188 26.2995C98.6024 26.3009 98.4869 26.2782 98.3797 26.2328C98.2724 26.1874 98.1757 26.1204 98.0956 26.0358C98.0155 25.9513 97.9538 25.8511 97.9143 25.7415C97.8748 25.632 97.8583 25.5155 97.866 25.3993ZM90.4776 23.9214C90.0981 23.9445 89.7421 24.1128 89.4834 24.3914C89.2247 24.6701 89.0833 25.0377 89.0885 25.4179C89.0937 25.7981 89.2451 26.1617 89.5113 26.4332C89.7774 26.7047 90.138 26.8632 90.518 26.876C90.9381 26.8906 91.349 26.75 91.6721 26.481L91.3732 26.0279C91.1407 26.2152 90.8521 26.3192 90.5536 26.3232C90.3514 26.34 90.1506 26.2774 89.9936 26.1488C89.8367 26.0202 89.7359 25.8357 89.7126 25.6341H91.8037L91.8156 25.3969C91.8334 25.2102 91.8121 25.0219 91.7531 24.844C91.6941 24.666 91.5987 24.5023 91.473 24.3633C91.3472 24.2242 91.1939 24.1129 91.0227 24.0364C90.8515 23.9599 90.6663 23.92 90.4788 23.919M90.4658 24.4658C90.6505 24.4629 90.8289 24.533 90.9621 24.661C91.0954 24.7889 91.1728 24.9643 91.1774 25.149H89.7173C89.7278 24.9602 89.8117 24.783 89.9511 24.6553C90.0906 24.5276 90.2744 24.4596 90.4634 24.4658M105.825 25.3981V22.8598H105.221V24.3329C105.117 24.1982 104.982 24.0904 104.827 24.0186C104.673 23.9468 104.503 23.913 104.333 23.9201C103.953 23.9388 103.596 24.1025 103.334 24.3775C103.072 24.6525 102.926 25.0177 102.926 25.3974C102.926 25.7772 103.072 26.1424 103.334 26.4174C103.596 26.6924 103.953 26.8562 104.333 26.8748C104.503 26.8821 104.672 26.8485 104.827 26.7769C104.981 26.7053 105.117 26.5977 105.221 26.4632V26.8048H105.834L105.825 25.3981ZM103.548 25.3981C103.557 25.2318 103.615 25.0717 103.714 24.9379C103.813 24.804 103.949 24.7022 104.106 24.6448C104.262 24.5875 104.432 24.5772 104.594 24.6153C104.756 24.6534 104.904 24.7381 105.018 24.859C105.133 24.98 105.209 25.1318 105.239 25.2958C105.268 25.4598 105.248 25.6288 105.183 25.7819C105.117 25.9349 105.008 26.0655 104.869 26.1572C104.73 26.249 104.567 26.2981 104.4 26.2983C104.284 26.2997 104.168 26.277 104.061 26.2317C103.954 26.1863 103.857 26.1192 103.777 26.0347C103.697 25.9501 103.635 25.8499 103.596 25.7404C103.556 25.6308 103.54 25.5143 103.548 25.3981ZM82.8698 23.9913H82.2578V24.3329C82.1534 24.1982 82.0183 24.0904 81.8637 24.0186C81.7092 23.9468 81.5397 23.913 81.3694 23.9201C80.9901 23.9388 80.6325 24.1025 80.3707 24.3775C80.1089 24.6525 79.9628 25.0177 79.9628 25.3974C79.9628 25.7772 80.1089 26.1424 80.3707 26.4174C80.6325 26.6924 80.9901 26.8562 81.3694 26.8748C81.5396 26.8821 81.709 26.8485 81.8636 26.7769C82.0181 26.7053 82.1533 26.5977 82.2578 26.4632V26.8048H82.8698V23.9913ZM80.5925 25.3981C80.602 25.2321 80.6598 25.0725 80.759 24.939C80.8581 24.8055 80.9941 24.704 81.1502 24.6468C81.3064 24.5897 81.4758 24.5796 81.6377 24.6177C81.7995 24.6557 81.9467 24.7403 82.061 24.861C82.1753 24.9817 82.2518 25.1332 82.2811 25.2969C82.3103 25.4606 82.291 25.6292 82.2256 25.782C82.1601 25.9348 82.0513 26.0652 81.9127 26.1569C81.774 26.2487 81.6116 26.2978 81.4453 26.2983C81.3289 26.2997 81.2134 26.277 81.1061 26.2317C80.9989 26.1863 80.9021 26.1192 80.8221 26.0347C80.742 25.9501 80.6802 25.8499 80.6407 25.7404C80.6012 25.6308 80.5848 25.5143 80.5925 25.3981ZM106.846 26.3968L106.958 26.4193L107.047 26.4774L107.109 26.5688L107.13 26.6767L107.109 26.7847L107.047 26.8736L106.958 26.9341L106.846 26.9566L106.728 26.9341L106.636 26.8736L106.576 26.7847L106.554 26.6767L106.576 26.5688L106.636 26.4774L106.728 26.4193L106.846 26.3968ZM106.846 26.8949L106.929 26.8771L106.998 26.8309L107.044 26.7621L107.061 26.6755L107.044 26.5913L106.998 26.5213L106.929 26.475L106.846 26.4609L106.76 26.475L106.692 26.5213L106.646 26.5913L106.629 26.6755L106.646 26.7621L106.692 26.8309L106.76 26.8771L106.846 26.8949ZM106.863 26.5391L106.94 26.5629L106.966 26.6222L106.946 26.6767L106.883 26.7016L106.968 26.8013H106.903L106.822 26.7028H106.798V26.8013H106.742V26.5391H106.863ZM106.798 26.5889V26.6589H106.862L106.897 26.6506L106.91 26.6233L106.897 26.5984L106.862 26.5902L106.798 26.5889Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M86.516 10.8858C86.5122 12.4265 86.8537 13.9484 87.5155 15.3397C88.1773 16.7309 89.1424 17.9562 90.3401 18.9254C91.5334 17.9528 92.4953 16.7267 93.1559 15.3361C93.8165 13.9455 94.1592 12.4253 94.1592 10.8858C94.1592 9.34631 93.8165 7.82611 93.1559 6.43553C92.4953 5.04495 91.5334 3.81885 90.3401 2.84625C89.143 3.81589 88.1782 5.04125 87.5165 6.43242C86.8547 7.82359 86.5129 9.34529 86.516 10.8858Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M96.8863 0.286633C94.5908 0.283181 92.3568 1.02909 90.5239 2.411C91.7992 3.42493 92.8292 4.71361 93.5372 6.18099C94.2452 7.64837 94.6129 9.25657 94.6129 10.8858C94.6129 12.5151 94.2452 14.1233 93.5372 15.5907C92.8292 17.0581 91.7992 18.3468 90.5239 19.3607C91.8671 20.3689 93.4306 21.0433 95.0856 21.3286C96.7407 21.6139 98.4398 21.5018 100.043 21.0015C101.646 20.5013 103.107 19.6271 104.307 18.4512C105.506 17.2753 106.408 15.8312 106.939 14.2381C107.471 12.6449 107.616 10.9484 107.363 9.28812C107.11 7.62786 106.466 6.05146 105.484 4.68892C104.502 3.32638 103.211 2.21666 101.716 1.45126C100.221 0.685855 98.5657 0.286696 96.8863 0.286633Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M86.0582 10.8858C86.0541 9.25593 86.42 7.64647 87.1285 6.17863C87.8369 4.7108 88.8694 3.42292 90.1479 2.41212C88.5736 1.22939 86.7009 0.508629 84.7398 0.330612C82.7787 0.152595 80.8068 0.524323 79.0453 1.40424C77.2837 2.28415 75.802 3.63745 74.7665 5.31228C73.7309 6.98711 73.1824 8.91724 73.1824 10.8864C73.1824 12.8555 73.7309 14.7857 74.7665 16.4605C75.802 18.1353 77.2837 19.4886 79.0453 20.3686C80.8068 21.2485 82.7787 21.6202 84.7398 21.4422C86.7009 21.2642 88.5736 20.5434 90.1479 19.3607C88.8693 18.3496 87.8369 17.0615 87.1285 15.5935C86.42 14.1255 86.0541 12.5158 86.0582 10.8858Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M166.814 5.80127V21.4419H163.043V5.80127H166.814ZM171.519 5.80127V8.7124H158.446V5.80127H171.519Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M149.665 5.80127V21.4419H145.894V5.80127H149.665ZM155.745 12.311V15.2114H148.591V12.311H155.745ZM156.357 5.80127V8.7124H148.591V5.80127H156.357Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M143.248 18.5415V21.4419H134.912V18.5415H143.248ZM136.254 5.80127V21.4419H132.484V5.80127H136.254ZM142.173 12.0103V14.814H134.912V12.0103H142.173ZM143.28 5.80127V8.7124H134.912V5.80127H143.28Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M224.148 20.1614H221.99V3.35458H227.712C228.395 3.3403 229.074 3.46152 229.71 3.71125C230.345 3.96097 230.926 4.33426 231.417 4.80957C231.913 5.25601 232.307 5.80353 232.574 6.41533C232.841 7.02712 232.974 7.68901 232.964 8.35652C232.978 9.0276 232.847 9.69381 232.58 10.3095C232.313 10.9252 231.916 11.4758 231.417 11.9236C230.416 12.8784 229.181 13.3553 227.712 13.3544H224.148V20.1614ZM224.148 11.2906H227.765C228.162 11.3024 228.557 11.2312 228.924 11.0816C229.292 10.9319 229.624 10.7072 229.9 10.4217C230.174 10.1547 230.392 9.83543 230.541 9.48268C230.69 9.12992 230.767 8.75086 230.767 8.36792C230.767 7.98497 230.69 7.60591 230.541 7.25316C230.392 6.9004 230.174 6.58111 229.9 6.31417C229.627 6.02265 229.296 5.79238 228.928 5.63864C228.56 5.4849 228.164 5.41119 227.765 5.4224H224.148V11.2906Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M237.935 8.28679C239.53 8.28679 240.788 8.71322 241.711 9.5661C242.634 10.419 243.095 11.5883 243.094 13.0742V20.1614H241.031V18.5656H240.937C240.043 19.8798 238.855 20.5368 237.373 20.5368C236.107 20.5368 235.048 20.1614 234.196 19.4104C233.787 19.0657 233.46 18.6341 233.239 18.1469C233.017 17.6597 232.908 17.1293 232.917 16.5943C232.917 15.4044 233.367 14.4581 234.265 13.7554C235.164 13.0527 236.364 12.7005 237.864 12.6987C239.145 12.6987 240.2 12.9334 241.029 13.4027V12.9092C241.032 12.5445 240.953 12.1838 240.799 11.8533C240.645 11.5228 240.419 11.2307 240.138 10.9983C239.568 10.4832 238.824 10.2021 238.056 10.2111C236.851 10.2111 235.897 10.7198 235.195 11.7372L233.295 10.5397C234.34 9.03775 235.887 8.28679 237.935 8.28679ZM235.144 16.6412C235.143 16.916 235.207 17.1872 235.331 17.4321C235.456 17.677 235.637 17.8886 235.86 18.0493C236.337 18.4249 236.929 18.6239 237.536 18.6125C238.446 18.611 239.319 18.2484 239.963 17.6041C240.677 16.9309 241.035 16.141 241.035 15.2345C240.362 14.6981 239.424 14.4299 238.221 14.4299C237.344 14.4299 236.614 14.6414 236.029 15.0642C235.438 15.4933 235.144 16.015 235.144 16.6412Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M254.939 8.66227L247.735 25.2304H245.509L248.182 19.4332L243.445 8.66227H245.79L249.213 16.9228H249.26L252.59 8.66227H254.939Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M215.43 11.8887C215.431 11.2308 215.375 10.5739 215.264 9.92549H206.166V13.6441H211.377C211.27 14.238 211.044 14.8042 210.713 15.3084C210.382 15.8127 209.952 16.2446 209.45 16.5782V18.992H212.56C214.381 17.3117 215.43 14.8269 215.43 11.8887Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M206.166 21.324C208.769 21.324 210.961 20.4685 212.56 18.9934L209.45 16.5796C208.584 17.1669 207.47 17.5022 206.166 17.5022C203.649 17.5022 201.514 15.8045 200.75 13.5167H197.546V16.0043C198.349 17.6034 199.58 18.9477 201.102 19.8871C202.625 20.8264 204.378 21.3239 206.166 21.324Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M200.75 13.5167C200.346 12.3179 200.346 11.0197 200.75 9.82089V7.33333H197.546C196.871 8.67857 196.519 10.1633 196.519 11.6688C196.519 13.1743 196.871 14.659 197.546 16.0043L200.75 13.5167Z'
                  fill='#EFE9DB'
                />
                <path
                  d='M206.166 5.83543C207.542 5.81294 208.871 6.33318 209.867 7.28371L212.62 4.52795C210.874 2.88667 208.561 1.98557 206.166 2.01357C204.378 2.01365 202.625 2.51116 201.102 3.45054C199.58 4.38993 198.349 5.73422 197.546 7.33333L200.75 9.82089C201.514 7.53314 203.649 5.83543 206.166 5.83543Z'
                  fill='#EFE9DB'
                />
                <defs>
                  <clipPath id='clip0_131_13414'>
                    <rect
                      width='48'
                      height='14.5848'
                      fill='white'
                      transform='translate(0.182373 6.3291)'
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>
        )}
        {/* Top Divider wave */}
        {showDividerWave && (
          <div className='absolute bottom-0 left-0 z-20 h-max w-full translate-y-[2px] transform overflow-hidden'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='w-[200%] fill-white md:w-full'
              width='1464'
              height='34'
              viewBox='0 0 1464 34'
              fill='none'
              preserveAspectRatio='none'
            >
              <path
                className='fill-white'
                d='M0 34H1464V13.9567C1462.14 14.0919 1459.76 14.2755 1456.86 14.4993L1456.79 14.5044C1400.39 18.8525 1148.67 38.2577 731.531 12.6924C315.789 -12.7874 65.0374 7.19974 7.26685 11.8046C4.35205 12.0369 1.92871 12.23 0 12.3763V34Z'
                fill='none'
              />
            </svg>
          </div>
        )}
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <m.div
            initial={false}
            className={`fixed left-0 top-0 z-[99999] h-screen w-screen bg-black/90`}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className='container-padding container relative flex h-full items-center justify-center'>
              <button
                className='absolute right-5 top-10 z-[100] rounded-full bg-white/10 px-4 py-4 text-white/90 transition duration-200 hover:bg-white/20 md:right-0'
                onClick={() => setActiveVideo(null)}
              >
                <svg
                  className='h-4 w-4 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  width='23'
                  height='23'
                  viewBox='0 0 23 23'
                  fill='none'
                >
                  <path
                    d='M21.5362 1.30971C21.8319 1.60542 21.9981 2.00649 21.9981 2.42469C21.9981 2.84289 21.8319 3.24396 21.5362 3.53967L3.69651 21.3794C3.4008 21.6751 2.99973 21.8412 2.58153 21.8412C2.16333 21.8412 1.76226 21.6751 1.46654 21.3794C1.17083 21.0837 1.0047 20.6826 1.0047 20.2644C1.0047 19.8462 1.17083 19.4451 1.46654 19.1494L19.3063 1.30971C19.602 1.014 20.003 0.847866 20.4212 0.847866C20.8394 0.847866 21.2405 1.014 21.5362 1.30971Z'
                    fill='currentColor'
                  />
                  <path
                    d='M21.5367 21.3807C21.241 21.6764 20.8399 21.8425 20.4217 21.8425C20.0035 21.8425 19.6025 21.6764 19.3068 21.3807L1.46705 3.54099C1.17134 3.24528 1.00521 2.84421 1.00521 2.42601C1.00521 2.00781 1.17134 1.60674 1.46705 1.31103C1.76276 1.01532 2.16383 0.849186 2.58203 0.849186C3.00023 0.849186 3.4013 1.01532 3.69701 1.31103L21.5367 19.1507C21.8324 19.4465 21.9986 19.8475 21.9986 20.2657C21.9986 20.6839 21.8324 21.085 21.5367 21.3807Z'
                    fill='currentColor'
                  />
                </svg>
              </button>
              {/* @ts-ignore  */}
              <YouTube
                videoId={activeVideo}
                opts={opts}
                className='aspect-video w-full max-w-4xl overflow-hidden rounded-md bg-black [aspect-ratio:_16_/_9]'
                iframeClassName='aspect-video h-full w-full [aspect-ratio:16_/_9]'
                onReady={onPlayerReady}
              />
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
